import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/useAuth.jsx";
import { toast } from 'sonner';
import { useTranslation } from "../contexts/TranslationContext.jsx";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { emailValidation, passwordValidation } from "../utils/validation.js";
import { decodeJwt } from "../utils/decodeJwt";
import { getHomePathForRole } from "../utils/roleHome";


function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { t, locale } = useTranslation();
  const { login } = useAuth();
  const { register, handleSubmit, trigger, formState: { errors, isSubmitted } } = useForm();

  // Revalida ao trocar de idioma para regenerar as mensagens de erro no idioma atual
  useEffect(() => {
    if (isSubmitted) trigger();
  }, [locale, isSubmitted, trigger]);

  async function handleLogin(data) {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: data.email, senha: data.senha }),
      });

      const responseData = await response.json();

      if (response.ok) {
        login({
          token: responseData.token,
          refreshToken: responseData.refreshToken,
        });

        toast.success(t('toasts.loginSuccess'));
        // Papel derivado do JWT (contém isAdmin e isPaid assinados pelo servidor)
        const payload = decodeJwt(responseData.token);
        navigate(getHomePathForRole({ isAdmin: payload?.isAdmin, isPaid: payload?.isPaid }), { replace: true });
      } else {
        toast.error(responseData.error || t('toasts.loginError'));
      }
    } catch {
      toast.error(t('toasts.connectionError'));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#111] border border-white/10 rounded-xl shadow-lg p-8">
        <h1 className="text-2xl font-bold text-center text-white mb-6">{t('login.title')}</h1>
        <form onSubmit={handleSubmit(handleLogin)} className="space-y-4" noValidate>
          <div>
            <input
              type="email"
              placeholder={t('login.emailPlaceholder')}
              {...register("email", emailValidation(t))}
              className={`w-full p-3 border rounded-md bg-[#0a0a0a] text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 ${
                errors.email ? 'border-red-500' : 'border-white/10'
              }`}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>
          <div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder={t('login.passwordPlaceholder')}
                {...register("senha", passwordValidation(t))}
                className={`w-full p-3 pr-12 border rounded-md bg-[#0a0a0a] text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 ${
                  errors.senha ? 'border-red-500' : 'border-white/10'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-500 transition"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.senha && (
              <p className="text-red-500 text-sm mt-1">{errors.senha.message}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-brand-500 hover:bg-brand-400 text-black p-3 rounded-md transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading && <Loader2 className="animate-spin" size={20} />}
            {t('login.button')}
          </button>
        </form>
        <Link to="/forgot-password" className="text-gray-400 hover:text-brand-500 text-sm mt-6 inline-block transition">
          {t('forgotPassword.title')}
        </Link>
        <p className="text-gray-400 text-sm mt-1">
          {t('nav.noAccount')}{" "}
          <Link to="/register" className="text-brand-500 hover:text-brand-400 font-medium transition">
            {t('nav.createAccount')}
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Login;
